import { IBookingDispute, IOpenDisputeRequest } from '../interfaces/heavyDutyMachinery/IBookingDispute';
import { apiService, apiUploadService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import { getLocalStorageKey } from '../helpers/localStorageHelper';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** At most one dispute per booking -- null when there isn't one. */
export const getBookingDispute = async (bookingId: string): Promise<IBookingDispute | null> => {
  const response = await apiService<IBookingDispute | null>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the claim');
};

export const openBookingDispute = async (bookingId: string, request: IOpenDisputeRequest): Promise<IBookingDispute> => {
  const response = await apiService<IBookingDispute>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE(bookingId),
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to open the claim');
};

/** The respondent's account. Stops the clock: a dispute that has been answered is a negotiation, not a countdown. */
export const respondToDispute = async (bookingId: string, response_: string): Promise<IBookingDispute> => {
  const response = await apiService<IBookingDispute>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_RESPOND(bookingId),
    method: HTTP_METHODS.POST,
    body: { response: response_ },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to send your reply');
};

export const offerDisputeAmount = async (bookingId: string, amount: number, note?: string): Promise<IBookingDispute> => {
  const response = await apiService<IBookingDispute>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_OFFERS(bookingId),
    method: HTTP_METHODS.POST,
    body: { amount, note },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to send your offer');
};

export const acceptDisputeOffer = async (bookingId: string, offerId: string): Promise<IBookingDispute> => {
  const response = await apiService<IBookingDispute>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_ACCEPT_OFFER(bookingId, offerId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to accept the offer');
};

export const escalateDispute = async (bookingId: string): Promise<IBookingDispute> => {
  const response = await apiService<IBookingDispute>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_ESCALATE(bookingId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to escalate');
};

export const withdrawDispute = async (bookingId: string): Promise<IBookingDispute> => {
  const response = await apiService<IBookingDispute>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_WITHDRAW(bookingId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to withdraw the claim');
};

export const uploadDisputeEvidence = async (bookingId: string, file: File, caption?: string): Promise<IBookingDispute> => {
  const formData = new FormData();
  formData.append('file', file);
  if (caption) formData.append('caption', caption);

  const response = await apiUploadService<IBookingDispute>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_EVIDENCE(bookingId),
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to upload the evidence');
};

/** Same authenticated-download-to-object-URL convention as the check-in/out photos -- evidence is never served from a bare URL. Caller owns revoking it. */
export const getDisputeEvidenceObjectUrl = async (bookingId: string, evidenceId: string): Promise<string> => {
  const accessToken = getLocalStorageKey('accessToken');
  const url = API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_EVIDENCE_DOWNLOAD(bookingId, evidenceId);
  const response = await fetch(`${heavyDutyMachineryBaseUrl}${url}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!response.ok) throw new Error(`Failed to download evidence (HTTP ${response.status})`);
  return window.URL.createObjectURL(await response.blob());
};
